// eslint-disable-next-line no-restricted-imports
import { css } from "@emotion/react";
// eslint-disable-next-line no-restricted-imports
import styled from "@emotion/styled";
import type { ComponentProps } from "react";
import { forwardRef } from "react";

import { Link } from "metabase/common/components/Link";
import { TreeNode } from "metabase/common/components/tree/TreeNode";
import { NAV_SIDEBAR_WIDTH } from "metabase/nav/constants";
import type { IconProps } from "metabase/ui";
import { Icon, Tooltip } from "metabase/ui";
import type { ColorName } from "metabase/ui/colors/types";

export const SidebarIcon = styled(
  forwardRef<SVGSVGElement, IconProps & { isSelected: boolean }>(
    function SidebarIcon({ isSelected, ...props }, ref) {
      return <Icon {...props} size={props.size ?? 16} ref={ref} />;
    },
  ),
)<{
  color?: ColorName | string;
  isSelected: boolean;
}>`
  ${(props) =>
    !props.color &&
    css`
      color: ${props.isSelected
        ? "var(--mb-color-text-selected)"
        : "var(--mb-color-core-brand)"};
    `}
`;

export const ExpandToggleButton = styled(TreeNode.ExpandToggleButton)`
  padding: 4px 0 4px 2px;
  color: var(--mb-color-core-brand);
`;

const selectedColorCSS = css`
  color: var(--mb-color-text-selected);
`;

function getTextColor(isSelected: boolean) {
  return isSelected
    ? "var(--mb-color-text-selected)"
    : "var(--mb-color-text-primary)";
}

type NodeRootProps = ComponentProps<typeof TreeNode.Root> & {
  hasDefaultIconStyle?: boolean;
};

export const NodeRoot = styled(TreeNode.Root)<NodeRootProps>`
  color: ${(props) => getTextColor(props.isSelected)};
  background-color: ${(props) =>
    props.isSelected ? "var(--mb-color-background_surface-selected)" : "unset"};
  /* Selection was otherwise distinguished only by a color/tint change
     (WCAG 1.4.1, style_color_misuse) — add a real border, not just color. */
  border-left-width: 3px;
  border-left-style: solid;
  border-left-color: ${(props) =>
    props.isSelected ? "var(--mb-color-text-selected)" : "transparent"};
  padding-left: ${(props) => props.depth}rem;
  border-radius: 4px;

  &:focus-within {
    outline: 2px solid var(--mb-color-input-focus);
    outline-offset: -2px;
  }

  ${ExpandToggleButton} {
    ${(props) => props.isSelected && selectedColorCSS}
  }

  &:hover {
    background-color: ${(props) =>
      props.isSelected
        ? "var(--mb-color-background_surface-selected)"
        : "var(--mb-color-background_surface-hover)"};
    color: ${(props) => getTextColor(props.isSelected)};

    ${ExpandToggleButton} {
      color: inherit;
    }
  }

  &:hover,
  &:focus,
  &:focus-within {
    ${SidebarIcon} {
      ${({ hasDefaultIconStyle = true, isSelected }) =>
        hasDefaultIconStyle &&
        (isSelected
          ? selectedColorCSS
          : css`
              color: var(--mb-color-text-primary);
            `)};
    }
  }
`;

const collectionDragAndDropHoverStyle = css`
  color: var(--mb-color-text-primary-inverse);
  background-color: var(--mb-color-core-brand);
`;

export const CollectionNodeRoot = styled(NodeRoot)<{ hovered?: boolean }>`
  ${(props) => props.hovered && collectionDragAndDropHoverStyle}
`;

const itemContentStyle = css`
  display: flex;
  align-items: center;
  width: 100%;
`;

export const FullWidthButton = styled.button<{ isSelected: boolean }>`
  color: inherit;
  cursor: pointer;

  ${itemContentStyle}
  ${TreeNode.NameContainer} {
    font-weight: 700;
    color: ${(props) =>
      props.isSelected ? "var(--mb-color-text-selected)" : "inherit"};
    text-align: start;

    &:hover {
      color: inherit;
    }
  }

  &:focus,
  &:focus-visible {
    outline: none;
  }
`;

export const FullWidthLink = styled(Link)`
  ${itemContentStyle}

  &:focus,
  &:focus-visible {
    outline: none !important;
  }
`;

export const FullWidthContainer = styled.div`
  ${itemContentStyle}
  cursor: pointer;
`;

const ITEM_NAME_LENGTH_TOOLTIP_THRESHOLD = 35;
const ITEM_NAME_LABEL_WIDTH = Math.round(parseInt(NAV_SIDEBAR_WIDTH, 10) * 0.7);

export const ItemName = styled(TreeNode.NameContainer)`
  /* max-width (not a fixed width): a specified width overrides the
     inherited flex:1 shrink behavior and doesn't respond to the sidebar's
     own narrower-breakpoint width (it switches to 90vw, but this stayed
     pinned at its normal-width value) — a long item name could then get
     clipped with no way to shrink further under browser zoom (WCAG 1.4.4).
     min-width:0 lets it actually shrink below that cap when needed. */
  max-width: ${ITEM_NAME_LABEL_WIDTH}px;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export function NameContainer({ children: itemName }: { children: string }) {
  if (itemName.length >= ITEM_NAME_LENGTH_TOOLTIP_THRESHOLD) {
    return (
      <Tooltip label={itemName} withArrow maw="none">
        <ItemName>{itemName}</ItemName>
      </Tooltip>
    );
  }
  return <TreeNode.NameContainer>{itemName}</TreeNode.NameContainer>;
}

export const LeftElementContainer = styled.div``;
export const RightElementContainer = styled.div``;
